// P0-B Batch 2 · PLAF Adjustment Drawer.
//
// Slide-out drawer for authoring a Payroll Line Adjustment.
// Multi-step: component picker → mode+value → three-value preview
// (with Net Pay delta) → reason + evidence-ref → submit.
//
// Contract:
//   - System Calculated → Admin Adjustment → Final Payroll terminology
//   - Statutory / compliance-impacting components get a visible badge
//   - "Requires Approval" banner shows when preview detects the
//     governance escalation (dual_signoff / variance breach)
//   - Preview never writes (calls /preview-adjustment)
//   - Submit label switches "Apply Adjustment" (medium) vs
//     "Submit for Approval" (high)
//   - Evidence-upload UI deferred per operator directive #9 — the
//     drawer accepts an evidence_object_id text reference.
//   - pay_pending (ATTENDANCE_SUMMARY): pays pending-regularization
//     days for this run without rewriting daily attendance status.
//
// Reversal mode is a simpler variant reused via ?mode=reverse.
import { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ShieldWarning, Info, Warning, CheckCircle } from "@phosphor-icons/react";

const STATUTORY_CODES = new Set([
    "TDS", "PF_EE", "ESI_EE", "PT", "LWF_EE",
]);

const MODE_META = {
    replace: { label: "replace" },
    delta: { label: "delta" },
    waive: { label: "waive" },
    add_one_time: { label: "add_one_time" },
    pay_pending: {
        label: "Pay Pending Days",
        hint: "Treats pending-regularization days as PAID for this run. The daily attendance status stays untouched and remains visible on the employee dashboard.",
    },
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (n) => {
    const v = Number(n || 0);
    const sign = v >= 0 ? "+" : "";
    return `${sign}${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function ThreeValueCard({ system, admin, final, componentCode }) {
    const isAtt = componentCode === "ATTENDANCE_SUMMARY";
    if (isAtt) {
        const sysPending = Number(system?.pending_regularization_days || 0);
        const finPendingPaid = Number(final?.pending_days_paid || 0);
        return (
            <div className="border rounded-lg bg-slate-50 p-3 text-xs">
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <div className="uppercase text-[10px] tracking-wider text-slate-500 mb-1">System Calculated</div>
                        <div className="font-mono">Paid: {Number(system?.paid_days || 0).toFixed(2)}</div>
                        <div className="font-mono">LOP: {Number(system?.lop_days || 0).toFixed(2)}</div>
                        <div className="font-mono text-yellow-800" data-testid="plaf-preview-system-pending">Pending: {sysPending}</div>
                    </div>
                    <div>
                        <div className="uppercase text-[10px] tracking-wider text-amber-700 mb-1">Admin Adjustment</div>
                        <div className="font-mono text-amber-900">Δ Paid: {signed(admin?.paid_days)}</div>
                        <div className="font-mono text-amber-900">Δ LOP: {signed(admin?.lop_days)}</div>
                        <div className="font-mono text-amber-900" data-testid="plaf-preview-admin-pending">Δ Pending paid: {signed(admin?.pending_days_paid)}</div>
                    </div>
                    <div>
                        <div className="uppercase text-[10px] tracking-wider text-emerald-700 mb-1">Final Payroll</div>
                        <div className="font-mono text-emerald-900 font-semibold" data-testid="plaf-preview-final-paid">Paid: {Number(final?.paid_days || 0).toFixed(2)}</div>
                        <div className="font-mono text-emerald-900 font-semibold" data-testid="plaf-preview-final-lop">LOP: {Number(final?.lop_days || 0).toFixed(2)}</div>
                        {sysPending > 0 && (
                            <div className="font-mono text-emerald-900" data-testid="plaf-preview-final-pending">
                                Pending paid: {finPendingPaid} of {sysPending}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="border rounded-lg bg-slate-50 p-3 text-xs">
            <div className="grid grid-cols-3 gap-3">
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-slate-500 mb-1">System Calculated</div>
                    <div className="font-mono text-sm" data-testid="plaf-preview-system">{inr(system)}</div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-amber-700 mb-1">Admin Adjustment</div>
                    <div className="font-mono text-sm text-amber-900" data-testid="plaf-preview-adjustment">{signed(admin)}</div>
                </div>
                <div>
                    <div className="uppercase text-[10px] tracking-wider text-emerald-700 mb-1">Final Payroll</div>
                    <div className="font-mono text-sm text-emerald-900 font-semibold" data-testid="plaf-preview-final">{inr(final)}</div>
                </div>
            </div>
        </div>
    );
}

function NetPayDeltaCard({ currentNet, proposedNet }) {
    const diff = Number(proposedNet || 0) - Number(currentNet || 0);
    const positive = diff >= 0;
    return (
        <div className="border rounded-lg bg-white p-3 text-xs" data-testid="plaf-preview-netpay">
            <div className="uppercase text-[10px] tracking-wider text-slate-500 mb-2">Net-Pay impact for this cycle</div>
            <div className="grid grid-cols-3 gap-3 items-baseline">
                <div>
                    <div className="text-[10px] text-slate-500">Current Net Pay</div>
                    <div className="font-mono text-sm">{inr(currentNet)}</div>
                </div>
                <div>
                    <div className="text-[10px] text-slate-500">Proposed Net Pay</div>
                    <div className="font-mono text-sm">{inr(proposedNet)}</div>
                </div>
                <div>
                    <div className="text-[10px] text-slate-500">Difference</div>
                    <div className={`font-mono text-sm font-semibold ${positive ? "text-emerald-700" : "text-red-700"}`} data-testid="plaf-preview-netpay-delta">{signed(diff)}</div>
                </div>
            </div>
        </div>
    );
}

export default function PLAFDrawer({
    open, onClose, runId, employeeId,
    prefillComponentCode = null,
    onSubmitted,
    line = null,   // current payroll line (used to compute Net-Pay preview)
    policies = [], // component policies for the picker
}) {
    const [step, setStep] = useState(1);
    const [componentCode, setComponentCode] = useState(prefillComponentCode || "");
    const [mode, setMode] = useState("");
    const [value, setValue] = useState("");
    const [attValue, setAttValue] = useState({ paid_delta: 0, lop_delta: 0, pending_days_to_pay: "" });
    const [reason, setReason] = useState("");
    const [evidenceObjectId, setEvidenceObjectId] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewErr, setPreviewErr] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            setStep(1);
            setComponentCode(prefillComponentCode || "");
            setMode(""); setValue(""); setAttValue({ paid_delta: 0, lop_delta: 0, pending_days_to_pay: "" });
            setReason(""); setEvidenceObjectId("");
            setPreview(null); setPreviewErr("");
        }
    }, [open, prefillComponentCode]);

    const policy = useMemo(() => {
        if (!componentCode) return null;
        // Exact match first, then parent-prefix match
        const exact = policies.find((p) => p.component_code === componentCode);
        if (exact) return exact;
        if (componentCode.includes(":")) {
            const parent = componentCode.split(":", 2)[0];
            return policies.find((p) => p.component_code === parent) || null;
        }
        return null;
    }, [componentCode, policies]);

    const allowedModes = policy?.allowed_modes || [];
    const isAttendance = componentCode === "ATTENDANCE_SUMMARY";
    const isStatutory = STATUTORY_CODES.has(componentCode)
        || (componentCode.split(":")[0] || "") in Object.fromEntries([...STATUTORY_CODES].map((c) => [c, true]));
    const policyForcesApproval = !!(policy?.dual_signoff_required) || policy?.behaviour === "approval_required";

    const buildSubmitValue = () => {
        if (isAttendance) {
            if (mode === "delta") {
                return { paid_delta: Number(attValue.paid_delta), lop_delta: Number(attValue.lop_delta) };
            }
            if (mode === "replace") {
                return { paid_days: Number(attValue.paid_days || 0), lop_days: Number(attValue.lop_days || 0) };
            }
            if (mode === "pay_pending") {
                // Blank → pay ALL pending days; number → partial payment.
                return attValue.pending_days_to_pay !== "" && attValue.pending_days_to_pay !== null
                    ? { pending_days_to_pay: Number(attValue.pending_days_to_pay) }
                    : null;
            }
            return null;
        }
        return mode === "waive" ? null : Number(value);
    };

    const runPreview = async () => {
        setPreviewLoading(true); setPreviewErr(""); setPreview(null);
        try {
            const res = await api.post(
                `/payroll/run/${runId}/employee/${employeeId}/preview-adjustment`,
                {
                    target: { component_code: componentCode, scope: isAttendance ? "structured" : "amount" },
                    mode, value: buildSubmitValue(), reason: "preview only — 10 char req",
                    evidence_object_id: null,
                },
            );
            setPreview(res.data);
        } catch (err) {
            setPreviewErr(formatApiError(err));
        } finally {
            setPreviewLoading(false);
        }
    };

    const requiresApproval = useMemo(() => {
        if (policyForcesApproval) return true;
        if (!preview) return false;
        const upper = policy?.upper_delta_pct;
        if (upper === null || upper === undefined) return false;
        const sys = Number(preview.system_value || 0);
        const fin = Number(preview.final_value || 0);
        if (sys <= 0) return false;
        return (Math.abs(fin - sys) / sys) > Number(upper);
    }, [preview, policy, policyForcesApproval]);

    const proposedNet = useMemo(() => {
        if (!preview || !line) return null;
        if (isAttendance) return null; // net-pay preview for attendance requires full engine — deferred
        // Estimate: replace the component's engine_computed_amount with final_value; recompute net.
        const findAmt = (rows) => (rows || []).find((r) => r.component_code === componentCode);
        const clone = (arr) => (arr || []).map((r) => ({ ...r }));
        const earnings = clone(line.earnings);
        const deductions = clone(line.deductions);
        let row = findAmt(earnings) || findAmt(deductions);
        const isNewRow = !row;
        if (isNewRow) {
            // add_one_time appended earning
            earnings.push({ component_code: componentCode, amount: Number(preview.final_value || 0) });
        } else {
            row.amount = Number(preview.final_value || 0);
        }
        const gross = earnings.reduce((s, r) => s + Number(r.amount || 0), 0);
        const total_ded = deductions.reduce((s, r) => s + Number(r.amount || 0), 0);
        return Math.round((gross - total_ded) * 100) / 100;
    }, [preview, line, componentCode, isAttendance]);

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await api.post(
                `/payroll/run/${runId}/employee/${employeeId}/adjustment`,
                {
                    target: { component_code: componentCode, scope: isAttendance ? "structured" : "amount" },
                    mode, value: buildSubmitValue(), reason,
                    evidence_object_id: evidenceObjectId || null,
                },
            );
            const pending = res.data?.adjustment?.status === "pending_approval" || res.data?.pending_approval;
            if (pending) {
                toast.warning(`Adjustment submitted for approval (AAS ${res.data.adjustment?.aas_action_id || res.data.action_id})`);
            } else {
                toast.success("Adjustment applied");
            }
            onSubmitted?.(res.data);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="w-full sm:w-[520px] overflow-y-auto" data-testid="plaf-drawer">
                <SheetHeader>
                    <SheetTitle>Add Payroll Adjustment</SheetTitle>
                </SheetHeader>

                {/* Step 1 · Component picker */}
                <section className="mt-4 space-y-2">
                    <Label>Component</Label>
                    <select
                        className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                        value={componentCode}
                        onChange={(e) => { setComponentCode(e.target.value); setMode(""); setPreview(null); }}
                        data-testid="plaf-component-picker"
                    >
                        <option value="">Select a component…</option>
                        {policies.map((p) => (
                            <option key={p.component_code} value={p.component_code}>
                                {p.component_code}{p.required_capability ? ` · ${p.required_capability}` : ""}
                            </option>
                        ))}
                    </select>
                    {isStatutory && componentCode && (
                        <Badge className="bg-red-100 text-red-800 border border-red-200" data-testid="plaf-statutory-badge">
                            <ShieldWarning size={12} className="mr-1" />Statutory / Compliance-impacting
                        </Badge>
                    )}
                    {policy?.behaviour === "evidence_required" && (
                        <div className="text-[11px] text-amber-800 flex items-center gap-1"><Info size={12} />This component requires evidence.</div>
                    )}
                </section>

                {/* Step 2 · Mode + value */}
                {componentCode && (
                    <section className="mt-4 space-y-2">
                        <Label>Mode</Label>
                        <div className="flex flex-wrap gap-2">
                            {allowedModes.map((m) => (
                                <Button
                                    key={m}
                                    variant={mode === m ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => { setMode(m); setPreview(null); }}
                                    data-testid={`plaf-mode-${m}`}
                                >
                                    {MODE_META[m]?.label || m}
                                </Button>
                            ))}
                        </div>
                        {mode === "pay_pending" && (
                            <Alert className="bg-yellow-50 border-yellow-200" data-testid="plaf-pay-pending-hint">
                                <Info size={14} className="mr-1 inline text-yellow-800" />
                                <AlertDescription className="text-yellow-900 text-xs">
                                    {MODE_META.pay_pending.hint}
                                </AlertDescription>
                            </Alert>
                        )}
                        {mode && isAttendance && mode === "pay_pending" && (
                            <div>
                                <Label className="mt-2">Days to pay (leave blank to pay all pending days)</Label>
                                <Input
                                    type="number" step="1" min="1"
                                    value={attValue.pending_days_to_pay}
                                    onChange={(e) => { setAttValue({ ...attValue, pending_days_to_pay: e.target.value }); setPreview(null); }}
                                    placeholder="e.g. 25 — blank pays every pending day"
                                    data-testid="plaf-pending-days-input"
                                />
                            </div>
                        )}
                        {mode && !isAttendance && mode !== "waive" && (
                            <div>
                                <Label className="mt-2">Value (₹)</Label>
                                <Input
                                    type="number" step="0.01"
                                    value={value}
                                    onChange={(e) => { setValue(e.target.value); setPreview(null); }}
                                    placeholder={mode === "delta" ? "e.g. -500 to reduce by 500" : "e.g. 3000"}
                                    data-testid="plaf-value-input"
                                />
                            </div>
                        )}
                        {mode && isAttendance && mode === "delta" && (
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label>Δ Paid days</Label>
                                    <Input type="number" step="0.5" value={attValue.paid_delta}
                                        onChange={(e) => setAttValue({ ...attValue, paid_delta: e.target.value })}
                                        data-testid="plaf-att-paid-delta" />
                                </div>
                                <div>
                                    <Label>Δ LOP days</Label>
                                    <Input type="number" step="0.5" value={attValue.lop_delta}
                                        onChange={(e) => setAttValue({ ...attValue, lop_delta: e.target.value })}
                                        data-testid="plaf-att-lop-delta" />
                                </div>
                            </div>
                        )}
                        {mode && (
                            <Button variant="outline" size="sm" onClick={runPreview} disabled={previewLoading} data-testid="plaf-preview-btn">
                                {previewLoading ? "Previewing…" : "Preview three-value effect"}
                            </Button>
                        )}
                        {previewErr && (
                            <Alert variant="destructive"><AlertDescription>{previewErr}</AlertDescription></Alert>
                        )}
                    </section>
                )}

                {/* Step 3 · Three-value preview + Net-pay impact */}
                {preview && (
                    <section className="mt-4 space-y-3">
                        <ThreeValueCard
                            system={preview.system_value}
                            admin={preview.admin_adjustment}
                            final={preview.final_value}
                            componentCode={componentCode}
                        />
                        {!isAttendance && line && (
                            <NetPayDeltaCard currentNet={line.net_pay} proposedNet={proposedNet} />
                        )}
                        {requiresApproval && (
                            <Alert className="bg-amber-50 border-amber-200 text-amber-900" data-testid="plaf-requires-approval">
                                <Warning size={14} className="mr-1 inline" />
                                <AlertTitle className="text-amber-900">Requires Approval</AlertTitle>
                                <AlertDescription className="text-amber-900 text-xs">
                                    {policyForcesApproval
                                        ? "Component policy requires dual sign-off. This adjustment will be routed to an authorised approver before it affects Final Payroll."
                                        : `Variance exceeds the ${Math.round(Number(policy?.upper_delta_pct || 0) * 100)}% policy threshold. Routed for dual sign-off.`}
                                </AlertDescription>
                            </Alert>
                        )}
                    </section>
                )}

                {/* Step 4 · Reason + evidence reference */}
                {preview && (
                    <section className="mt-4 space-y-3">
                        <div>
                            <Label>Reason (min 10 characters)</Label>
                            <Textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Provide operator-readable justification…"
                                data-testid="plaf-reason"
                                rows={3}
                            />
                        </div>
                        <div>
                            <Label>Evidence reference (existing object id — optional)</Label>
                            <Input
                                value={evidenceObjectId}
                                onChange={(e) => setEvidenceObjectId(e.target.value)}
                                placeholder="e.g. ROBJ-xxxx (paste an existing document reference)"
                                data-testid="plaf-evidence-id"
                            />
                            <div className="text-[10px] text-slate-500 mt-1">File-upload UI wired in a follow-up batch — evidence field remains supported.</div>
                        </div>
                        <Button
                            className="w-full"
                            disabled={submitting || reason.trim().length < 10}
                            onClick={submit}
                            data-testid="plaf-submit"
                        >
                            {submitting ? "Submitting…" : requiresApproval ? "Submit for Approval" : "Apply Adjustment"}
                        </Button>
                    </section>
                )}
            </SheetContent>
        </Sheet>
    );
}


// -------------------------------------------------------------------
// Reversal sub-drawer
// -------------------------------------------------------------------


export function PLAFReversalDrawer({ open, onClose, runId, employeeId, adjustmentId, componentCode, onSubmitted }) {
    const [reason, setReason] = useState("");
    const [evidenceObjectId, setEvidenceObjectId] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (!open) { setReason(""); setEvidenceObjectId(""); } }, [open]);

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await api.post(
                `/payroll/run/${runId}/employee/${employeeId}/adjustment/${adjustmentId}/reverse`,
                { reason, evidence_object_id: evidenceObjectId || null },
            );
            const pending = res.data?.reversal?.status === "pending_approval" || res.data?.pending_approval;
            toast[pending ? "warning" : "success"](pending ? "Reversal submitted for approval" : "Reversal applied");
            onSubmitted?.(res.data);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };
    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="w-full sm:w-[460px]" data-testid="plaf-reversal-drawer">
                <SheetHeader>
                    <SheetTitle>Reverse Adjustment</SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-3">
                    <div className="text-xs text-slate-600">
                        Reversing adjustment on <span className="font-mono">{componentCode}</span>.
                        Reversal will inherit the target component&apos;s governance policy.
                    </div>
                    <div>
                        <Label>Reversal reason (min 10 characters)</Label>
                        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="plaf-reversal-reason" />
                    </div>
                    <div>
                        <Label>Evidence reference (optional)</Label>
                        <Input value={evidenceObjectId} onChange={(e) => setEvidenceObjectId(e.target.value)} data-testid="plaf-reversal-evidence" />
                    </div>
                    <Button
                        className="w-full"
                        variant="destructive"
                        disabled={submitting || reason.trim().length < 10}
                        onClick={submit}
                        data-testid="plaf-reversal-submit"
                    >
                        {submitting ? "Submitting…" : "Confirm Reversal"}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
